import { apiFetch } from "./api";

/**
 * ADR-0172 — the Atlas Provider's entity-view catalog is substrate, owned by
 * the hub (`src/platformProvider/catalog.ts`) and served over HTTP. This
 * module is a READER, not a second author: entities, params, defaults, and
 * columns reach the inspector from the catalog route, and there deliberately
 * is no local copy to drift (the nodeCreationShapes G10 pattern).
 *
 * Fallback, deliberate: if the catalog is unavailable, the inspector renders
 * an empty entity list with its load error — never a guessed catalog. An
 * unconfigured node is loudly reported by the validator either way.
 */

export type PlatformParamSpec =
  | { kind: "enum_set"; values: string[]; default: string[] }
  | { kind: "account_ids" }
  | { kind: "group_ids" }
  | { kind: "int"; min: number; max: number; default: number };

export type PlatformColumnSpec = { name: string; type: "string" | "number" | "date" | "boolean" };

export type PlatformCatalogView = {
  key: string;
  label: string;
  description: string;
  params: Record<string, PlatformParamSpec>;
  columns: PlatformColumnSpec[];
};

type CatalogResponse = { views: PlatformCatalogView[] };
type Fetcher = () => Promise<CatalogResponse>;

let cache: PlatformCatalogView[] | null = null;
let inFlight: Promise<void> | null = null;

const defaultFetcher: Fetcher = () => apiFetch<CatalogResponse>("/api/tenant/platform-entity-catalog");

/**
 * Load the catalog once per session. Concurrent callers share one request; a
 * failure clears the in-flight promise so a later mount retries rather than
 * latching the first failure.
 */
export async function loadPlatformEntityCatalog(fetcher: Fetcher = defaultFetcher): Promise<void> {
  if (cache) return;
  if (inFlight) return inFlight;
  inFlight = fetcher()
    .then((res) => {
      cache = res.views ?? [];
    })
    .catch(() => {
      cache = null;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** The served views — empty until loaded; NEVER a local fallback. */
export function platformEntityViews(): PlatformCatalogView[] {
  return cache ?? [];
}

export function platformEntityView(key: string): PlatformCatalogView | null {
  return (cache ?? []).find((v) => v.key === key) ?? null;
}

/** True once the hub's catalog is in hand — the inspector explains a degraded state off this. */
export function platformEntityCatalogLoaded(): boolean {
  return cache !== null;
}

export function resetPlatformEntityCatalogForTests(): void {
  cache = null;
  inFlight = null;
}
